import React, { useEffect, useRef, useState } from 'react';
import { View, Modal } from 'react-native';
import { useInfiniteQuery } from '@tanstack/react-query';

import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { MosaicPreset, cameraService } from '../../../services/central-api/cameras';
import { SelectableItem } from '../../../components/SelectableList';
import { SelectablePaginatedList } from '../../../components/SelectableListPaginated';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { MyCheckbox } from '../../../components/MyCheckbox';
import CameraTagsDropdown from '../../../components/Dropdowns/CameraTagsDropdown';
import PresetDropdown from './PresetDropdown';
import useDebounce from '../../../components/hooks/useDebounce';
import { useForm } from 'react-hook-form';

interface FromMosaic {
    title: string;
    preset: MosaicPreset;
    displayCameraTitle: boolean;
}

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    mosaic?: Mosaic;
}

const PAGE_SIZE = 100;

export default function MosaicEditModal({ onClose, mosaic, isModalVisible }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const isFetchingRef = useRef(false);
    const [selectedTags, setSelectedTags] = useState<number[]>([]);
    const [selectedCameras, setSelectedCameras] = useState<SelectableItem[]>([]);
    const [textFilter, setTextFilter] = useState<string>('');
    const debouncedFilter = useDebounce(textFilter, 500);
    const [selectableCameras, setSelectableCameras] = useState<SelectableItem[]>([]);

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['cameras', selectedTags, debouncedFilter],
        enabled: isModalVisible,
        queryFn: ({ pageParam = 0 }) =>
            cameraService.getPaginatedMosaicCameras({
                page: pageParam,
                limit: PAGE_SIZE,
                textFilter: debouncedFilter || undefined,
                tagsIds: selectedTags.length ? selectedTags : undefined
            }),
        getNextPageParam: (lastPage, pages) =>
            lastPage.rows.length == PAGE_SIZE ? pages.length : undefined,
    });

    useEffect(() => {
        if (!isModalVisible || !data) {
            return;
        }

        const items = data.pages
            .flatMap(page => page.rows)
            .filter(cam => !selectedCameras.some(sel => sel.id === cam.id))
            .map(cam => ({
                id: cam.id,
                name: cam.title,
            }));

        setSelectableCameras(items);
    }, [data, selectedCameras, isModalVisible]);

    async function getMosaic(mosaic: Mosaic) {
        const res = await cameraService.getMosaic(mosaic.id);
        form.reset({ ...res });
        setSelectedCameras(
            res.cameras.filter(c => c.camera !== null).map(c => ({
                id: c.camera!.id,
                name: c.camera!.title
            }))
        );
    }

    useEffect(() => {
        setTextFilter(textFilter);
    }, [debouncedFilter]);

    useEffect(() => {
        if (!mosaic || !isModalVisible) {
            return;
        }
        getMosaic(mosaic);
    }, [mosaic, isModalVisible]);

    function clearFilters() {
        setSelectedTags([]);
        setTextFilter('');
        setSelectedCameras([]);
    }

    const defaultEmptyValues: FromMosaic = {
        title: '',
        preset: MosaicPreset['4_PRESET'],
        displayCameraTitle: false,
    };

    const form = useForm<FromMosaic>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    async function formSubmit() {
        try {
            const data = form.getValues();
            if (mosaic) {
                await cameraService.updateMosaic({
                    ...data,
                    id: mosaic.id,
                }, selectedCameras.map(c => String(c.id)));
            } else {
                await cameraService.createMosaic({
                    ...data,
                    camerasToAdd: selectedCameras.map(c => String(c.id)),
                });
            }

            form.reset(defaultEmptyValues);
            clearFilters();
            onClose();
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully')
            });
        } catch (err) {
            if (err instanceof ClientError) {
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message)
                });
                return;
            }

            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError')
            });
        }
    }

    useEffect(() => {
        isFetchingRef.current = false;
    }, [textFilter, selectedTags]);

    return (
        <Modal visible={isModalVisible} animationType='fade' onRequestClose={() => onClose} transparent={true}>
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ flexDirection: 'row', columnGap: 10, zIndex: 4 }}>
                        <View style={{ flex: 1 }}>
                            <FormInput
                                label={translate('title')}
                                form={form}
                                fieldName='title'
                                required={true}
                            />
                        </View>
                        <PresetDropdown
                            form={form}
                            fieldName='preset'
                            zIndex={2}
                            required={true}
                            disabled={false}
                        />
                    </View>
                    <CameraTagsDropdown
                        value={selectedTags}
                        setValue={setSelectedTags}
                        zIndex={3}
                        labelFontSize={14}
                    />
                    <View>
                        <MyCheckbox
                            label={translate('displayCameraTitle')}
                            checked={form.watch('displayCameraTitle')}
                            setChecked={value => form.setValue('displayCameraTitle', value)}
                        />
                    </View>
                    <SelectablePaginatedList
                        selectableLabel={translate('availableCameras')}
                        selectedLabel={translate('selectedCameras')}
                        selectableItems={selectableCameras}
                        selectedItems={selectedCameras}
                        onAddItem={(item) =>
                            setSelectedCameras(prev => [...prev, item])
                        }
                        onRemoveItem={(item) =>
                            setSelectedCameras(prev => prev.filter(i => i.id !== item.id))
                        }
                        onLoadMoreSelectable={async () => {
                            if (!hasNextPage || isFetchingRef.current) {
                                return;
                            }
                            isFetchingRef.current = true;
                            try {
                                await fetchNextPage();
                            } finally {
                                isFetchingRef.current = false;
                            }
                        }}
                        isLoadingMoreSelectable={isFetchingNextPage}
                        onSelectableSearchChange={(value) => {
                            setTextFilter(value);
                        }}
                        onAddAll={() => {
                            setSelectedCameras(prev => [...prev, ...selectableCameras]);
                        }}
                        onRemoveAll={() => {
                            setSelectedCameras([]);
                        }}

                    />

                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => {
                            form.reset(defaultEmptyValues);
                            onClose();
                            clearFilters();
                        }}
                        disabled={!form.formState.isValid}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        backgroundColor: '#31313199',
        justifyContent: 'center',
        alignItems: 'center',
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : '100%',
        maxWidth: 850,
        height: isMobile ? '80%' : '100%',
        maxHeight: 650,
        backgroundColor: theme.colors.card.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 15,
        zIndex: 2
    }),
}));
